import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { Button } from '@/components/ui/button'
import { DEPARTMENT_IDS } from '@/constants/departments'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { Loader2, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { getEmployeeContains } from '@/modules/Employee/services'
import { addSaleFollower } from '../../services'

interface SaleFollowerCellProps {
    customerId?: number
    /** Tên người đang theo dõi; rỗng nghĩa là chưa ai theo dõi. */
    names: string[]
    /** Gọi sau khi gán xong để cập nhật ngay tại chỗ. */
    onAssigned: (employeeName: string) => void
}

/**
 * Đã có người theo dõi thì chỉ hiển thị — muốn sửa phải vào trang khách hàng.
 * Chưa có ai thì cho gán thẳng tại đây, khỏi phải rời báo cáo.
 *
 * Cập nhật tại chỗ thay vì refetch: enricher của @mgmt cache thông tin khách
 * theo customer_id (đo được ~15 phút), nên gọi lại ngay vẫn trả bản cũ.
 */
const SaleFollowerCell = ({
    customerId,
    names,
    onAssigned,
}: SaleFollowerCellProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const { control, watch, reset } = useForm<{ employeeId: number | null }>({
        defaultValues: { employeeId: null },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: addSaleFollower },
        {
            successMessage: 'Đã gán sale theo dõi',
            errorMessage: 'Gán sale theo dõi thất bại',
            reactQueryKeys: [],
        }
    )

    if (names.length > 0) {
        return <span className="text-sm">{names.join(', ')}</span>
    }

    if (!customerId) {
        return <span className="text-muted-foreground">—</span>
    }

    if (!isOpen) {
        return (
            <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setIsOpen(true)}
            >
                <UserPlus className="mr-1 h-3.5 w-3.5" />
                Gán sale
            </Button>
        )
    }

    const handleSelect = async () => {
        const employeeId = watch('employeeId')
        if (!employeeId) return
        try {
            await mutateAsync({ customerId, employeeId })
            const [option] = await getEmployeeContains({
                employee_ids: [employeeId],
            })
            onAssigned(option?.label || `#${employeeId}`)
            setIsOpen(false)
            reset()
        } catch {
            // useMutationWithHandler đã hiện toast lỗi
        }
    }

    return (
        <div className="flex items-center gap-1">
            <div className="min-w-[180px]">
                <EmployeeSelect
                    control={control}
                    name="employeeId"
                    showLabel={false}
                    placeholder="Chọn nhân viên"
                    onlyActive
                    departmentIds={[
                        DEPARTMENT_IDS.ACCOUNT,
                        DEPARTMENT_IDS.SALES,
                        DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                    ]}
                />
            </div>
            <Button
                size="sm"
                className="h-7 px-2 text-xs"
                disabled={isPending || !watch('employeeId')}
                onClick={handleSelect}
            >
                {isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                    'Lưu'
                )}
            </Button>
            <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => {
                    setIsOpen(false)
                    reset()
                }}
            >
                Huỷ
            </Button>
        </div>
    )
}

export default SaleFollowerCell
